import SnapType from './snapType';
import LineOrientation from './lineOrientation';
import BaseCanvasPositioningHelper from './baseCanvasPositioningHelper';
import { FAMILY_PREVIEW_EDITOR_CONFIG as CONFIG } from '@/constants/familyPreviewEditor';

class CanvasClusterPositioningHelper extends BaseCanvasPositioningHelper {
  constructor() {
    super();
  }

  getAlignmentLines(clusterNode, stage) {
    const possibleAlignmentLines = this.getPossibleAlignmentLines(clusterNode, stage);
    const rectangleNode = this.getRectangleNode(clusterNode);
    const clusterBounds = this.getNodeSnappingEdges(rectangleNode);

    return this.getClosestAlignmentLines(possibleAlignmentLines, clusterBounds, CONFIG.canvasPositioningHelper.cluster.minimalStepSize);
  };

  getEqualDistanceAlignment(clusterNode, stage) {
    const allClusters = stage.find(`.${CONFIG.global.cluster.nodeName}`);

    if (allClusters.length < 3) {
      return {
        equalDistanceIndicators: [],
        alignmentLines: []
      };
    };

    if (this.isClustersIntersection(clusterNode, allClusters)) {
      return {
        equalDistanceIndicators: [],
        alignmentLines: []
      };
    };

    const currentClusterRect = this.getRectangle(clusterNode);

    const equalDistanceIndicators = [];
    let alignmentLine = {};

    const rightSideClusters = [];
    const leftSideClusters = [];

    allClusters
      .filter(cluster => cluster !== clusterNode)
      .forEach(cluster => cluster.getClientRect().x > currentClusterRect.x
        ? rightSideClusters.push(cluster)
        : leftSideClusters.push(cluster)
      );

    let closestClusters = [];

    if (leftSideClusters.length === 0) {
      // Current cluster is first in row
      closestClusters = rightSideClusters
        .sort((a, b) => a.getClientRect().x - b.getClientRect().x)
        .slice(0, 2);

      const firstRect = this.getRectangle(closestClusters[0]);
      const secondRect = this.getRectangle(closestClusters[1]);

      const distanceBetweenClosestClusters = secondRect.x - (firstRect.x + firstRect.width);

      const currentClusterRightSideXCoord = currentClusterRect.x + currentClusterRect.width;
      const distanceToClosestCluster = firstRect.x - currentClusterRightSideXCoord;

      const difference = Math.abs(distanceToClosestCluster - distanceBetweenClosestClusters);

      if (difference < CONFIG.canvasPositioningHelper.cluster.minimalStepSize) {
        const alignedXCoordinate = firstRect.x - distanceBetweenClosestClusters;

        equalDistanceIndicators.push({
          fromCoordX: alignedXCoordinate,
          fromCoordY: firstRect.y + firstRect.height,
          toCoordX: firstRect.x,
          toCoordY: firstRect.y + firstRect.height,
          orientation: LineOrientation.Horizontal
        });

        equalDistanceIndicators.push({
          fromCoordX: firstRect.x + firstRect.width,
          fromCoordY: firstRect.y + firstRect.height,
          toCoordX: secondRect.x,
          toCoordY: firstRect.y + firstRect.height,
          orientation: LineOrientation.Horizontal
        });

        const isCurrentClusterHigherThanComparable = currentClusterRect.y + currentClusterRect.height > firstRect.y + firstRect.height;

        alignmentLine = {
          line: alignedXCoordinate,
          coordinates: {
            fromCoordX: alignedXCoordinate,
            fromCoordY: isCurrentClusterHigherThanComparable
              ? currentClusterRect.y + currentClusterRect.height
              : currentClusterRect.y,
            toCoordX: alignedXCoordinate,
            toCoordY: firstRect.y + firstRect.height
          },
          offset: currentClusterRect.x - alignedXCoordinate,
          orientation: LineOrientation.Vertical,
          snap: SnapType.End
        };
      }
    }

    if (leftSideClusters.length > 0 && rightSideClusters.length > 0) {
      // Current cluster is between other clusters
      const leftCluster = leftSideClusters.reduce((prev, current) =>
        (!!prev && prev.getClientRect().x > current.getClientRect().x) ? prev : current);
      const rightCluster = rightSideClusters.reduce((prev, current) =>
        (!!prev && prev.getClientRect().x < current.getClientRect().x) ? prev : current);

      const leftRectangle = this.getRectangle(leftCluster);
      const rightRectangle = this.getRectangle(rightCluster);

      const leftClusterRightSideCoordX = leftRectangle.x + leftRectangle.width;
      const currentClusterRightSideCoordX = currentClusterRect.x + currentClusterRect.width;

      const distanceToLeftCluster = currentClusterRect.x - leftClusterRightSideCoordX;
      const distanceToRightCluster = rightRectangle.x - currentClusterRightSideCoordX;

      const equalDistance = (distanceToLeftCluster + distanceToRightCluster) / 2;

      equalDistanceIndicators.push({
        fromCoordX: leftClusterRightSideCoordX,
        fromCoordY: leftRectangle.y + leftRectangle.height,
        toCoordX: leftClusterRightSideCoordX + equalDistance,
        toCoordY: leftRectangle.y + leftRectangle.height,
        orientation: LineOrientation.Horizontal
      });

      equalDistanceIndicators.push({
        fromCoordX: rightRectangle.x - equalDistance,
        fromCoordY: rightRectangle.y + rightRectangle.height,
        toCoordX: rightRectangle.x,
        toCoordY: rightRectangle.y + rightRectangle.height,
        orientation: LineOrientation.Horizontal
      });

      const isCurrentClusterHigherThanComparable = currentClusterRect.y + currentClusterRect.height > leftRectangle.y + leftRectangle.height;

      alignmentLine = {
        line: leftClusterRightSideCoordX + equalDistance,
        coordinates: {
          fromCoordX: leftClusterRightSideCoordX + equalDistance,
          fromCoordY: isCurrentClusterHigherThanComparable
            ? currentClusterRect.y + currentClusterRect.height
            : currentClusterRect.y,
          toCoordX: leftClusterRightSideCoordX + equalDistance,
          toCoordY: leftRectangle.y + leftRectangle.height
        },
        offset: currentClusterRect.x - (leftClusterRightSideCoordX + equalDistance),
        orientation: LineOrientation.Vertical,
        snap: SnapType.Start
      };
    }

    if (rightSideClusters.length === 0) {
      // Current cluster is last in row
      closestClusters = leftSideClusters
        .sort((a, b) => a.getClientRect().x - b.getClientRect().x)
        .slice(-2);

      const firstRect = this.getRectangle(closestClusters[0]);
      const secondRect = this.getRectangle(closestClusters[1]);

      const distanceBetweenClosestClusters = secondRect.x - (firstRect.x + firstRect.width);
      const distanceToClosestCluster = currentClusterRect.x - (secondRect.x + secondRect.width);

      const difference = Math.abs(distanceToClosestCluster - distanceBetweenClosestClusters);

      if (difference < CONFIG.canvasPositioningHelper.cluster.minimalStepSize) {
        const alignedXCoordinate = secondRect.x + secondRect.width + distanceBetweenClosestClusters;

        equalDistanceIndicators.push({
          fromCoordX: Math.round(firstRect.x + firstRect.width),
          fromCoordY: Math.round(firstRect.y + firstRect.height),
          toCoordX: Math.round(secondRect.x),
          toCoordY: Math.round(firstRect.y + firstRect.height),
          orientation: LineOrientation.Horizontal
        });

        equalDistanceIndicators.push({
          fromCoordX: Math.round(secondRect.x + secondRect.width),
          fromCoordY: Math.round(secondRect.y + secondRect.height),
          toCoordX: Math.round(alignedXCoordinate),
          toCoordY: Math.round(secondRect.y + secondRect.height),
          orientation: LineOrientation.Horizontal
        });

        const isCurrentClusterHigherThanComparable = currentClusterRect.y + currentClusterRect.height > secondRect.y + secondRect.height;

        alignmentLine = {
          line: alignedXCoordinate,
          coordinates: {
            fromCoordX: alignedXCoordinate,
            fromCoordY: isCurrentClusterHigherThanComparable
              ? currentClusterRect.y + currentClusterRect.height
              : currentClusterRect.y,
            toCoordX: alignedXCoordinate,
            toCoordY: secondRect.y + secondRect.height
          },
          offset: currentClusterRect.x - alignedXCoordinate,
          orientation: LineOrientation.Vertical,
          snap: SnapType.Start
        };
      }
    }

    return {
      equalDistanceIndicators: equalDistanceIndicators,
      alignmentLines: [alignmentLine]
    };
  }; 

  getPossibleAlignmentLines(clusterNode, stage) {
    const vertical = [];
    const horizontal = [];

    stage.find(`.${CONFIG.global.cluster.nodeName}`).forEach(cluster => {
      if (cluster === clusterNode) {
        return;
      }

      var rect = cluster.getClientRect();

      vertical.push([rect.x, rect.x + rect.width, rect.x + rect.width / 2]);
      horizontal.push([rect.y, rect.y + rect.height, rect.y + rect.height / 2]);
    });

    return {
      vertical: vertical.flat(),
      horizontal: horizontal.flat()
    };
  };

  getRectangle(node) {
    return this.getRectangleNode(node).getClientRect();
  }

  getRectangleNode(node) {
    return node.find(`.${CONFIG.rectangle.nodeName}`)[0];
  }

  isClustersIntersection(currentCluster, allClusters) {
    const otherClusters = allClusters.filter(cluster => cluster !== currentCluster);

    const currentLeftBorder = currentCluster.getClientRect().x;
    const currentRightBorder = currentCluster.getClientRect().x + currentCluster.getClientRect().width;

    for (const cluster of otherClusters) {
      const rect = cluster.getClientRect();

      const leftBorder = rect.x;
      const rightBorder = rect.x + rect.width;

      if (leftBorder <= currentLeftBorder && rightBorder >= currentLeftBorder
        || leftBorder <= currentRightBorder && rightBorder >= currentRightBorder) {
        return true;
      }
    }
    
    return false;
  }
}

export default new CanvasClusterPositioningHelper();